/**
 * صفحه‌ی «برنامه‌های من» — قرینه‌ی تبِ اولِ موبایل، apps/mobile/app/(tabs)/index.tsx
 * (WHOSUP-135؛ از WHOSUP-165 آن تب خودش صفحه‌ی برنامه‌هاست).
 *
 * از WHOSUP-157 تقویم و لیست **با هم** دیده می‌شن، نه پشتِ یک سوییچ:
 * روی صفحه‌ی پهن لیست ستونِ اصلیه و تقویمِ ماه توی ستونِ کناری می‌چسبه و
 * موقع اسکرول همراه می‌مونه (طرحِ ۳ که کاربر انتخاب کرد). روی صفحه‌ی باریک
 * فقط دو هفته‌ی جاری از تقویم می‌آد، لیست زیرش، و راهنما (لجند) و بنر اصلاً
 * نیستن — خواستِ صریحِ تیکت.
 *
 * دسته‌بندی و شبکه‌ی ماه/دوهفته از core/lib/planBoard.ts می‌آید؛ رنگ‌ها فقط از
 * توکن‌ها (var(--accent) و خانواده‌اش در base.css) — و رنگ تنها حاملِ معنا
 * نیست: هر دسته شکل خودش را دارد و راهنمای کنارِ تقویمِ پهن اسمش را می‌گوید.
 *
 * WHOSUP-235: سربرگِ روزهای هفته بالای هر دو شبکه آمد، و دو هفته‌ی خالیِ
 * صفحه‌ی باریک دیگر کشیده نمی‌شود (قرینه‌ی موبایل). ماهِ صفحه‌ی پهن این قاعده
 * را ندارد — آن‌جا ورق زدن هست و تقویم خودش راهِ گشتنه.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { signalRepo } from '@/core/data/signalRepo';
import { notificationRepo, tagRepo } from '@/core/data/miscRepo';
import { tagIdsOf } from '@/core/domain/types';
import { useSession } from '@/core/session';
import { getLocale, t, useLocale } from '@/core/lib/i18n';
import { formatWeekdayDate, signalWhenLabel, weekdayHeads } from '@/core/lib/dates';
import {
  dayKey, hasPlansIn, monthGridPadded, planEntries, planRowLabelKeys, splitEntries, twoWeekGrid, weekStartFor,
} from '@/core/lib/planBoard';
import type { MonthCell, PlanBucket, PlanEntry } from '@/core/lib/planBoard';
import { Card } from '@/ui/kit';
import { CategoryIcon } from '@/ui/icons';
import { reportBrokenImage } from '@/core/lib/report';
import { PageBanner } from '@/ui/PageBanner';
import { useIsNarrow } from '@/ui/useIsNarrow';

const BUCKET_LABEL: Record<PlanBucket, string> = {
  confirmedMine: 'planConfirmedMine',
  confirmedOther: 'planConfirmedOther',
  inPending: 'planInPending',
  maybeish: 'planMaybe',
  unanswered: 'planUnanswered',
};
const BUCKETS = Object.keys(BUCKET_LABEL) as PlanBucket[];

const Pip = ({ bucket }: { bucket: PlanBucket }) => (
  <span className={`plan-pip ${bucket}`} aria-hidden="true" />
);

/**
 * بندانگشتیِ رخداد کنارِ هر ردیف (WHOSUP-165) — قرینه‌ی قابِ نمای مجله‌ایِ
 * کشفِ موبایل: قاب بی‌عکس هم می‌مونه (آیکونِ دسته روی زمینه)، تا ردیفِ
 * بی‌عکس کنارِ عکس‌دار نیمه‌کاره به‌نظر نرسه و همه از یه جا شروع شن.
 * عکس فقط برای سیگنالیه که از رخدادِ کاتالوگ زاده شده (signal.event)؛ و
 * چون خیلی از عکس‌ها از سایتِ منبع می‌آن و اون‌ور پاک می‌شن، آدرسِ خراب
 * هم می‌ره سراغِ همون جانشین، نه مستطیلِ عکسِ شکسته.
 */
/**
 * آمارِ پاسخ‌ها زیرِ برنامه‌ی خودم (WHOSUP-281) — قرینه‌ی `Counts` در
 * apps/mobile/src/features/plans/PlanCard.tsx: همان عددها (`signal.counts` از
 * `withCounts` در core)، همان قاعده‌ی «صفرها را نشان نده جز هستم». فقط برای
 * سازنده: تصمیمِ من به جوابِ بقیه ربطی ندارد، و RLS هم برای بقیه عددی نمی‌دهد.
 */
function EntryCounts({ e }: { e: PlanEntry }) {
  const { profile } = useSession();
  const c = e.signal.counts;
  if (!c || e.signal.creatorId !== profile?.id) return null;
  return (
    <span className="counts">
      <span className="count ok">{t('inCount', { n: c.in })}</span>
      {c.maybe > 0 ? <span className="count">{t('maybeCount', { n: c.maybe })}</span> : null}
      {c.conditional > 0
        ? <span className="count">{t('condCount', { n: c.conditional })}</span> : null}
      {c.pending > 0
        ? <span className="count wait">{t('pendingCount', { n: c.pending })}</span> : null}
    </span>
  );
}

function EntryRow({ e, counts }: { e: PlanEntry; counts?: boolean }) {
  const [broken, setBroken] = useState(false);
  const myId = useSession().profile?.id ?? '';
  // همون کلیدِ کشِ صفحه‌ی کشف — در عمل یه درخواست برای کلِ اپ.
  const tags = useQuery({ queryKey: ['tags'], queryFn: tagRepo.list });
  const img = e.signal.event?.imageUrl;
  const slug = (tags.data ?? []).find((x) => x.id === tagIdsOf(e.signal)[0])?.slug;
  return (
    <Link className="plan-entry" to={`/signal/${e.signal.id}`}>
      <span className={e.signal.status === 'held' ? 'plan-entry-held' : undefined}
        style={e.signal.status === 'held' ? undefined : { display: 'contents' }}
        title={e.signal.status === 'held' ? t('heldTitle') : undefined}>
      {img && !broken
        ? <img className="plan-entry-thumb" src={img} alt="" loading="lazy"
            onError={() => { reportBrokenImage(img, 'web.plans'); setBroken(true); }} />
        : (
          <span className="plan-entry-thumb" aria-hidden="true">
            <CategoryIcon slug={slug ?? 'other'} size={22} />
          </span>
        )}
      </span>
      <Pip bucket={e.bucket} />
      <span className="plan-entry-main">
        <span>{e.signal.title}</span>
        <span className="dim">
          {e.when ? formatWeekdayDate(e.when) : signalWhenLabel(e.signal) || t('plansUndated')}
          {e.signal.plan?.finalPlace ? ` · ${e.signal.plan.finalPlace}`
            : e.signal.placeName ? ` · ${e.signal.placeName}` : ''}
          {' — '}
          {planRowLabelKeys(e.signal, myId).map((k) => t(k)).join(' / ')}
        </span>
        {counts ? <EntryCounts e={e} /> : null}
      </span>
    </Link>
  );
}

export function PlansPage() {
  useLocale();
  const narrow = useIsNarrow();
  const { profile, loading } = useSession();
  const qc = useQueryClient();
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  // تبِ فید حذف شد و نقطه‌ی «سیگنال/رخداد» حالا روی همین تبه — پس دیدنِ
  // همین صفحه باید همون دسته‌ها رو «دیده‌شده» بزنه (همون کاری که HomePage
  // می‌کنه)، وگرنه نقطه‌ای می‌موند که از هیچ راهِ ناوبری پاک نمی‌شد.
  useEffect(() => {
    if (!profile) return;
    notificationRepo.markSeen(profile.id, 'rest')
      .then(() => qc.invalidateQueries({ queryKey: ['unreadByCategory'] }))
      .catch(() => undefined);
  }, [profile?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const plans = useQuery({
    queryKey: ['myPlans'],
    queryFn: () => signalRepo.myPlans(profile!.id),
    enabled: !!profile,
  });

  const entries = useMemo(
    () => planEntries(plans.data ?? [], profile?.id ?? ''),
    [plans.data, profile?.id],
  );
  const split = useMemo(() => splitEntries(entries, new Date()), [entries]);
  const byDay = useMemo(() => {
    const m = new Map<string, PlanEntry[]>();
    for (const e of entries) {
      if (!e.when) continue;
      const k = dayKey(e.when);
      m.set(k, [...(m.get(k) ?? []), e]);
    }
    return m;
  }, [entries]);

  /**
   * روی صفحه‌ی باریک دو هفته‌ی جاری، وگرنه ماهِ انتخاب‌شده — هر دو از core.
   * شبکه‌ی ماه «پرشده» است: خانه‌های اول/آخر روزهای واقعیِ ماهِ قبل/بعدند
   * (کم‌رنگ)، نه خانه‌ی خالی — به خواستِ کاربر.
   */
  const weekStart = weekStartFor(getLocale());
  const weeks: MonthCell[][] = useMemo(
    () => (narrow
      ? twoWeekGrid(new Date(), weekStart)
        .map((row) => row.map((day) => ({ day, inMonth: true })))
      : monthGridPadded(month.y, month.m, weekStart)),
    [narrow, month, weekStart],
  );
  const heads = useMemo(() => weekdayHeads(weekStart), [weekStart]);
  // دو هفته‌ی بی‌برنامه فقط یک شبکه‌ی خاموشِ بالای صفحه است — نگاه کن به hasPlansIn.
  const showCalendar = !narrow
    || hasPlansIn(weeks.map((w) => w.map((c) => c.day)), byDay);
  const monthTitle = new Intl.DateTimeFormat(getLocale(), { month: 'long', year: 'numeric' })
    .format(new Date(month.y, month.m, 1));
  const todayKey = dayKey(new Date().toISOString());

  if (loading || plans.isLoading) return <div className="feed"><p className="dim">…</p></div>;
  if (!profile) return <div className="feed"><Card><p className="dim">{t('oneStepLeft')}</p></Card></div>;

  const section = (title: string, list: PlanEntry[]) => (list.length ? (
    <Card>
      <h3>{title}</h3>
      {list.map((e) => <EntryRow key={e.signal.id} e={e} counts />)}
    </Card>
  ) : null);

  const lists = entries.length > 0 ? (
    <>
      {section(t('plansUpcoming'), split.upcoming)}
      {section(t('plansUndated'), split.undated)}
      {section(t('plansPast'), split.past)}
    </>
  ) : null;

  const calendar = !showCalendar ? null : (
    <Card>
      {/* ورق زدنِ ماه فقط روی صفحه‌ی پهن — دو هفته‌ی جاری ورق نمی‌خوره. */}
      {narrow ? null : (
        <div className="plan-month-nav">
          <button type="button" className="chip" aria-label="previous month"
            onClick={() => {
              setSelectedDay(null);
              setMonth((p) => (p.m === 0 ? { y: p.y - 1, m: 11 } : { y: p.y, m: p.m - 1 }));
            }}>
            ‹
          </button>
          <h3>{monthTitle}</h3>
          <button type="button" className="chip" aria-label="next month"
            onClick={() => {
              setSelectedDay(null);
              setMonth((p) => (p.m === 11 ? { y: p.y + 1, m: 0 } : { y: p.y, m: p.m + 1 }));
            }}>
            ›
          </button>
        </div>
      )}

      {/* سربرگِ روزهای هفته — همان شبکه‌ی ۷ستونی، پس ستون به ستون سرِ جاش. */}
      <div className="plan-week-heads" aria-hidden="true">
        {heads.map((h, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <span key={i} className="dim">{h}</span>
        ))}
      </div>

      <div className="plan-grid">
        {weeks.flat().map(({ day, inMonth }) => {
          const items = byDay.get(day) ?? [];
          return (
            <button
              key={day}
              type="button"
              className={[
                'plan-day',
                inMonth ? '' : 'out',
                day === selectedDay ? 'sel' : '',
                day === todayKey ? 'today' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => setSelectedDay(day)}>
              <span className="dim">{Number(day.slice(8))}</span>
              <span className="plan-pips">
                {items.slice(0, 4).map((e) => <Pip key={e.signal.id} bucket={e.bucket} />)}
              </span>
            </button>
          );
        })}
      </div>

      {selectedDay ? (byDay.get(selectedDay) ?? []).map((e) => (
        <EntryRow key={e.signal.id} e={e} />
      )) : null}

      {/* راهنما — چون رنگ به‌تنهایی نباید حاملِ معنا باشد. روی صفحه‌ی باریک
          به خواستِ تیکت نیست؛ آن‌جا برچسبِ دسته تهِ هر ردیفِ لیست هست. */}
      {narrow ? null : (
        <div className="plan-legend">
          {BUCKETS.map((b) => (
            <span key={b} className="plan-legend-row">
              <Pip bucket={b} />
              <span className="dim">{t(BUCKET_LABEL[b])}</span>
            </span>
          ))}
        </div>
      )}
    </Card>
  );

  const empty = entries.length === 0
    ? <Card><p className="dim">{t('plansEmpty')}</p></Card> : null;

  if (narrow) {
    return (
      <div className="feed">
        <h1 className="feed-title">{t('myPlans')}</h1>
        {empty}
        {calendar}
        {lists}
      </div>
    );
  }

  return (
    <div className="feed plans-page">
      <PageBanner kind="plans" />
      <h1 className="feed-title">{t('myPlans')}</h1>
      {empty}
      <div className="plans-cols">
        <div className="plans-list">{lists}</div>
        <aside className="plans-cal">{calendar}</aside>
      </div>
    </div>
  );
}
